import {computed, Injectable, signal} from "@angular/core"
import {
    checkHasValidRatio,
    checkHasValidResolutions,
    editsJsonHasUnsupportedOperator,
    TextureSetRevisionSpecific,
    TextureTypeData,
} from "@app/textures/texture-editor/texture-editor-callback"
import {ProcessingJobStateFragment, TextureEditorDataObjectFragment} from "@app/textures/service/textures-api.generated"
import {getSimpleJobState, SimpleJobState} from "app/textures/utils/simple-job-state"
import {Size2} from "@cm/math"
import {TextureType} from "@generated"

@Injectable()
export class TextureEditorDataService {
    readonly $revision = signal<TextureSetRevisionSpecific | undefined>(undefined)
    readonly $selectedTextureType = signal<TextureType | undefined>(undefined)
    readonly $editsProcessingJob = signal<ProcessingJobStateFragment | null | undefined>(undefined)

    setRevision(revision: TextureSetRevisionSpecific | undefined): void {
        this.$revision.set(revision)
    }

    updateRevision(patch: (current: TextureSetRevisionSpecific) => TextureSetRevisionSpecific): void {
        const current = this.$revision()
        if (current) {
            this.$revision.set(patch(current))
        }
    }

    selectTextureType(type: TextureType): void {
        this.$selectedTextureType.set(type)
    }

    // purely a lookup into $revision().typeData - every available type's data is fetched upfront, so switching
    // between types is instant, no fetch/loading state to manage here
    readonly $textureTypeData = computed((): TextureTypeData | undefined => {
        const type = this.$selectedTextureType()
        return type ? this.$revision()?.typeData.get(type) : undefined
    })

    // derived, never fetched/stored - stays correct after a resize without any special-casing
    readonly $pxPerCm = computed((): Size2 | undefined => {
        const revision = this.$revision()
        const resultDataObject = this.$textureTypeData()?.resultDataObject
        if (!revision || !resultDataObject?.width || !resultDataObject.height) {
            return undefined
        }
        return new Size2(resultDataObject.width / revision.textureSetRevision.width, resultDataObject.height / revision.textureSetRevision.height)
    })

    readonly $isInUse = computed(() => (this.$revision()?.textureSetRevision.materialRevisionReferences.length ?? 0) > 0)

    readonly $hasValidResolutions = computed(() => {
        const revision = this.$revision()
        return revision ? checkHasValidResolutions(revision.textureSetRevision) : true
    })

    readonly $hasValidRatio = computed(() => {
        const revision = this.$revision()
        return revision ? checkHasValidRatio(revision.textureSetRevision) : true
    })

    readonly $isLegacyUnsupportedEdit = computed(() => {
        const revision = this.$revision()
        return revision ? editsJsonHasUnsupportedOperator(revision.textureSetRevision.editsJson) : false
    })

    readonly $isProcessingEdits = computed(() => {
        const jobState = this.$editsProcessingJob()?.state
        return !!jobState && getSimpleJobState(jobState) !== SimpleJobState.Complete
    })

    readonly $processingJobId = computed(() => {
        if (!this.$isProcessingEdits()) return undefined
        return this.$editsProcessingJob()?.id
    })

    readonly $canEditDisplacementMap = computed(() => {
        const revision = this.$revision()
        return !revision?.autoGenerateDisplacementMap || this.$selectedTextureType() !== TextureType.Displacement
    })

    readonly $availableTextureTypesForOperators = computed(() => {
        const revision = this.$revision()
        if (!revision) {
            return []
        } else if (revision.autoGenerateDisplacementMap) {
            return revision.availableTextureTypes.filter((textureType) => textureType !== TextureType.Displacement)
        } else {
            return revision.availableTextureTypes
        }
    })

    // the one computed both OperatorStack (to drive the HAL source image) and the component (to drive canvas
    // display info) need: which data object is actually being displayed for the current selection
    readonly $displayDataObject = computed((): {dataObject: TextureEditorDataObjectFragment; isResult: boolean} | undefined => {
        const revision = this.$revision()
        const type = this.$selectedTextureType()
        const data = this.$textureTypeData()
        if (!revision || !type || !data) {
            return undefined
        }
        const isResult = this.$isLegacyUnsupportedEdit() || (revision.autoGenerateDisplacementMap && type === TextureType.Displacement)
        const dataObject = isResult ? data.resultDataObject : data.sourceDataObject
        return dataObject && {dataObject, isResult}
    })
}
